/**
 * El precio, mientras se escribe.
 *
 * QUE PROBLEMA RESUELVE
 * ---------------------
 * En TORA el precio de venta NO se escribe: sale de un trigger que combina
 * costo, markup, las dos monedas y el redondeo comercial. El que carga un
 * producto elige los insumos, no el resultado, y sin este panel el resultado
 * recien aparece despues de guardar — o peor, en el momento de vender.
 *
 * Por eso el panel muestra la cuenta ENTERA y no solo el numero final:
 *
 *     US$ 150            costo
 *     × 1.550            el dolar de hoy (solo si las monedas difieren)
 *     = $ 232.500        el costo en la moneda de venta
 *     + $ 81.375  (35%)  el markup
 *     = $ 313.875        antes de redondear
 *     + $ 125            redondeo al mil, siempre para arriba
 *     = $ 314.000        precio de venta
 *
 * Cada linea contesta una pregunta distinta, y la del dolar contesta la mas
 * importante de todas: por que este precio va a cambiar solo la semana que
 * viene.
 *
 * DOS FORMAS, UNA SOLA CUENTA
 * ---------------------------
 * Se dibuja de dos maneras segun donde viva:
 *
 *  - `columna` (default), la tarjeta alta de siempre. Es la que va en la
 *    pantalla de revision, al lado del resto del resumen.
 *  - `banda`, horizontal, para cuando el panel va debajo del formulario y tiene
 *    todo el ancho del dialogo. Ahi la cuenta se lee de izquierda a derecha,
 *    que es como se lee una cuenta.
 *
 * Los pasos salen de `pasosDe()` una sola vez y despues se dibujan de una forma
 * o de la otra. Sin eso serian dos listas que hay que acordarse de mantener
 * iguales, y en algun momento dejarian de decir lo mismo.
 *
 * Los numeros salen de `simularPrecio()`, que llama a `calcularPrecio()` de
 * `@tora/shared` — la misma funcion que replica el SQL. Lo que se ve aca es lo
 * que va a guardar la base.
 */
import { ArrowRight, Pencil, TrendingUp } from "lucide-react";
import { formatear, formatearNumero } from "@tora/shared";

import { ChipMoneda, Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { cn } from "@/lib/utils";

import type { FormularioProducto, Simulacion } from "./logica";

export interface SimuladorPrecioProps {
  form: FormularioProducto;
  simulacion: Simulacion;
  cotizacion: number;
  /** Horizontal, para cuando el panel va debajo del formulario a todo el ancho. */
  banda?: boolean;
  className?: string;
}

interface PasoDato {
  termino: string;
  valor: string;
  atenuado?: boolean;
  destacado?: boolean;
}

const ETIQUETA_CORTA_REDONDEO: Record<string, string> = {
  NINGUNO: "",
  UNIDAD: "al entero",
  CIEN: "al cien",
  MIL: "al mil",
  TERMINACION_990: "a terminación 990",
};

/**
 * La cuenta, paso por paso. Una sola vez, para las dos formas de dibujarla.
 *
 * Los valores son texto y no `<Importe>`: la lista mezcla plata con la
 * cotizacion ("US$ 1 = $ 1.550") y sumandos con signo ("+ $ 81.375"), y es una
 * cuenta que se lee de corrido, no una columna de importes que se comparan.
 */
function pasosDe(form: FormularioProducto, simulacion: Simulacion, cotizacion: number): PasoDato[] {
  const moneda = form.precioMoneda;
  const convierte = form.costoMoneda !== moneda;

  return [
    { termino: "Costo", valor: formatear(form.costo ?? 0, form.costoMoneda) },
    ...(convierte
      ? [
          {
            termino: "Cotización de hoy",
            valor: `US$ 1 = $ ${formatearNumero(cotizacion, 4)}`,
            atenuado: true,
          },
          {
            termino: `Costo en ${moneda === "ARS" ? "pesos" : "dólares"}`,
            valor: formatear(simulacion.costoBase, moneda),
          },
        ]
      : []),
    {
      termino:
        form.markupTipo === "PORCENTAJE"
          ? `Markup ${formatearNumero(form.markupValor ?? 0)} %`
          : `Markup fijo ${formatear(form.markupValor ?? 0, form.markupMoneda)} ${form.markupMoneda}`,
      valor: `+ ${formatear(simulacion.markupImporte, moneda)}`,
    },
    { termino: "Antes de redondear", valor: formatear(simulacion.precioBruto, moneda) },
    ...(form.redondeo !== "NINGUNO"
      ? [
          {
            termino: `Redondeo ${ETIQUETA_CORTA_REDONDEO[form.redondeo]}`,
            valor: `+ ${formatear(simulacion.ajusteRedondeo, moneda)}`,
            atenuado: true,
          },
        ]
      : []),
    // El calculado va solo si NO coincide con el numero grande. Sin precio
    // manual son el mismo importe, y ponerlo igual era escribirlo dos veces.
    ...(simulacion.esManual
      ? [
          {
            termino: "Debería costar",
            valor: formatear(simulacion.precioCalculado, moneda),
          },
        ]
      : []),
  ];
}

export function SimuladorPrecio({
  form,
  simulacion,
  cotizacion,
  banda = false,
  className,
}: SimuladorPrecioProps): JSX.Element {
  const moneda = form.precioMoneda;

  if (!simulacion.calculable) {
    return (
      <Card
        variant="dashed"
        padding="normal"
        className={cn(
          banda ? "flex flex-wrap items-baseline gap-x-3 gap-y-1" : "flex flex-col gap-1",
          className,
        )}
      >
        <TituloSeccion nivel="etiqueta" as="span">
          Precio de venta
        </TituloSeccion>
        <p className="text-sm text-muted-foreground">{simulacion.motivo}</p>
      </Card>
    );
  }

  const pasos = pasosDe(form, simulacion, cotizacion);

  const rotulo = (
    <TituloSeccion nivel="etiqueta" as="span">
      {simulacion.esManual ? "Precio de venta (manual)" : "Precio de venta"}
    </TituloSeccion>
  );

  // Sin chip: ya esta al lado del rotulo, como en un Kpi.
  const precio = (
    <span data-testid="precio-final">
      <Importe
        monto={simulacion.precioFinal}
        moneda={moneda}
        codigo={false}
        tamano="xl"
        className="font-semibold"
      />
    </span>
  );

  // "A mano" es una marca del precio, no un estado: outline con icono.
  const aMano = simulacion.esManual ? (
    <Badge variant="outline">
      <Pencil aria-hidden="true" />
      Escrito a mano
    </Badge>
  ) : null;

  /*
    El precio manual es la unica forma de contradecir el motor, y es legitima: a
    veces el precio lo pone el mercado y no la planilla. Lo que no puede pasar es
    que se contradiga sin verlo. Por eso al lado del manual va SIEMPRE el
    calculado y la diferencia entre los dos.
  */
  const avisoManual = simulacion.esManual ? (
    <Aviso tono="warning" titulo="Este precio no lo calcula el sistema" className="w-full">
      <p className="tabular-nums">
        Debería costar <Importe monto={simulacion.precioCalculado} moneda={moneda} codigo={false} />{" "}
        y lo estás vendiendo a{" "}
        <Importe monto={simulacion.precioFinal} moneda={moneda} codigo={false} />:{" "}
        <strong className="font-medium">
          {simulacion.desvio >= 0 ? "+" : "−"}
          {formatear(Math.abs(simulacion.desvio), moneda)} ({simulacion.desvioPct >= 0 ? "+" : "−"}
          {formatearNumero(Math.abs(simulacion.desvioPct))} %)
        </strong>
        .
      </p>
      <p className="text-muted-foreground">
        Mientras esté a mano, el precio no se repricia cuando cambia el costo ni cuando se mueve el
        dólar.
      </p>
    </Aviso>
  ) : null;

  const ganancia = (
    <span className="inline-flex items-center gap-1.5">
      <TrendingUp className="size-3.5" aria-hidden="true" />
      Ganancia <Importe monto={simulacion.ganancia} moneda={moneda} codigo={false} tamano="sm" /> ·
      margen{" "}
      <span className="tabular-nums text-foreground">
        {formatearNumero(simulacion.margenPct)} %
      </span>
    </span>
  );

  const repricia =
    simulacion.dependeDelDolar && !simulacion.esManual ? (
      <span className="inline-flex items-center gap-1">
        <ArrowRight className="size-3.5" aria-hidden="true" />
        Se repricia solo cuando cambia el dólar
      </span>
    ) : null;

  // ── Horizontal ──
  if (banda) {
    return (
      <Card
        data-testid="simulador-precio"
        acento={moneda}
        padding="normal"
        className={cn("flex flex-wrap items-end gap-x-6 gap-y-4", className)}
      >
        {/*
          Los pasos son `flex-auto`: reparten el espacio sobrante entre ellos en
          vez de amontonarse a la izquierda. Sin eso quedaba un hueco muerto
          entre el ultimo paso y el precio, que es el mismo ancho desperdiciado
          que este panel vino a resolver.
        */}
        <dl className="flex min-w-0 flex-1 flex-wrap items-end gap-x-6 gap-y-3">
          {pasos.map((p) => (
            <PasoBanda key={p.termino} {...p} />
          ))}
        </dl>

        <div className="flex shrink-0 flex-col items-end gap-0.5 border-l border-border pl-6">
          <div className="flex items-baseline gap-2">
            {rotulo}
            <ChipMoneda moneda={moneda} />
          </div>
          <div className="flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1">
            {precio}
            {aMano}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {ganancia}
            {repricia}
          </div>
        </div>

        {avisoManual}
      </Card>
    );
  }

  // ── Vertical ──
  return (
    <Card
      data-testid="simulador-precio"
      acento={moneda}
      padding="normal"
      className={cn("flex flex-col gap-3", className)}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {rotulo}
        <ChipMoneda moneda={moneda} />
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {precio}
        {aMano}
      </div>

      {/*
        La cuenta paso por paso. Va siempre, no detras de un "ver detalle": el
        detalle ES la explicacion de por que el numero de arriba es ese.
      */}
      <dl className="space-y-1 border-t border-border pt-3 text-sm">
        {pasos.map((p) => (
          <PasoColumna key={p.termino} {...p} />
        ))}
      </dl>

      {avisoManual}

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
        {ganancia}
        {repricia}
      </div>
    </Card>
  );
}

function PasoColumna({
  termino,
  valor,
  atenuado = false,
  destacado = false,
}: PasoDato): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-xs text-muted-foreground">{termino}</dt>
      <dd
        className={cn(
          "min-w-0 text-right tabular-nums",
          atenuado ? "text-xs text-muted-foreground" : "text-sm",
          destacado && "font-semibold",
        )}
      >
        {valor}
      </dd>
    </div>
  );
}

/**
 * En la banda el valor va ARRIBA y el termino abajo: lo que se recorre de
 * izquierda a derecha son los numeros, y la etiqueta esta para consultarla, no
 * para leerla en cada paso. El `flex-col-reverse` deja el `dt` antes que el
 * `dd` en el DOM —que es lo que una lista de definiciones exige— y los muestra
 * al reves.
 */
function PasoBanda({ termino, valor, atenuado = false, destacado = false }: PasoDato): JSX.Element {
  return (
    <div className="flex min-w-0 flex-auto flex-col-reverse gap-0.5">
      <dt className="whitespace-nowrap text-xs leading-tight text-muted-foreground">{termino}</dt>
      <dd
        className={cn(
          "whitespace-nowrap tabular-nums",
          atenuado ? "text-xs text-muted-foreground" : "text-sm",
          destacado && "font-semibold",
        )}
      >
        {valor}
      </dd>
    </div>
  );
}
